import { useEffect, useState } from "react";
import { Loader2, Sparkles, ChevronDown, MapPin, Clock, Car } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

import { fetchQuote, money } from "@/lib/quote";
import { cn } from "@/lib/utils";

const LOADING_MESSAGES = [
  "Calculating your fare...",
  "Finding the optimal route...",
  "Applying premium pricing...",
];

/**
 * Fetches and displays an itemised, GST-inclusive quote.
 * Props:
 *  - payload: /api/quote request body (or null)
 *  - dark: dark theme
 *  - title: heading label
 *  - defaultOpen: start with the breakdown expanded (checkout uses this)
 *  - onQuote(result)
 */
export default function QuoteSummary({
  payload,
  dark = false,
  title = "Estimated Fare",
  defaultOpen = false,
  onQuote,
}) {
  const [state, setState] = useState({ loading: false, data: null, error: null });
  const [open, setOpen] = useState(defaultOpen);
  const [loadingIdx, setLoadingIdx] = useState(0);

  const key = payload ? JSON.stringify(payload) : null;

  useEffect(() => {
    if (!payload) {
      setState({ loading: false, data: null, error: null });
      return;
    }
    let active = true;
    setState((s) => ({ ...s, loading: true, error: null }));
    const t = setTimeout(async () => {
      try {
        const data = await fetchQuote(payload);
        if (!active) return;
        setState({ loading: false, data, error: null });
        if (onQuote) onQuote(data);
      } catch (e) {
        if (active) setState({ loading: false, data: null, error: "Unable to calculate a price right now." });
      }
    }, 500);
    return () => {
      active = false;
      clearTimeout(t);
    };
  }, [key]);

  // Rotate loading copy while calculating.
  useEffect(() => {
    if (!state.loading) return;
    const id = setInterval(() => setLoadingIdx((i) => (i + 1) % LOADING_MESSAGES.length), 1500);
    return () => clearInterval(id);
  }, [state.loading]);

  const t = dark
    ? { label: "text-white/50", value: "text-white/90", total: "sjc-gold", sub: "text-white/45", divide: "border-white/10", chip: "bg-white/5", chipText: "text-white/70", panel: "" }
    : { label: "text-zinc-500", value: "text-zinc-800", total: "text-zinc-900", sub: "text-zinc-400", divide: "border-zinc-200", chip: "bg-zinc-50", chipText: "text-zinc-700", panel: "border border-zinc-200" };

  const { loading, data, error } = state;

  const Head = (
    <div className="flex items-center justify-between">
      <span className={cn("text-[11px] font-semibold tracking-[0.25em] uppercase", t.label)}>
        {title}
      </span>
      {loading && (
        <span className={cn("text-xs flex items-center gap-1.5", t.sub)}>
          <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.5} />
          <span className="min-w-[130px]">{LOADING_MESSAGES[loadingIdx]}</span>
        </span>
      )}
    </div>
  );

  if (!payload) {
    return (
      <div data-testid="quote-summary-empty" className={cn("rounded-xl p-5", dark ? "" : "border border-dashed border-zinc-200")}>
        {Head}
        <p className={cn("mt-3 text-sm leading-relaxed", t.sub)}>
          Enter pickup, drop-off and vehicle to see your fare.
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="quote-summary-error" className={cn("rounded-xl p-5", t.panel)}>
        {Head}
        <p className={cn("mt-3 text-sm", t.sub)}>{error} Please call us on +61 400 000 000 for a manual quote.</p>
      </div>
    );
  }

  if (data && data.success === false) {
    return (
      <div data-testid="quote-summary-noprice" className={cn("rounded-xl p-5", t.panel)}>
        {Head}
        <p className={cn("mt-3 text-sm", t.sub)}>
          We couldn&apos;t auto-price this route. Submit your request and we&apos;ll confirm a fare shortly.
        </p>
      </div>
    );
  }

  const li = data?.line_items;
  const meta = data?.meta || {};
  const hasJourney = meta.trip_km || meta.trip_minutes;
  const vehicleName = data?.vehicle?.name;

  return (
    <div data-testid="quote-summary" className={cn("rounded-xl p-5", t.panel)}>
      {Head}
      {data ? (
        <>
          <div className={cn("mt-4 flex items-baseline justify-between")}>
            <span className={cn("text-[11px] font-semibold tracking-[0.25em] uppercase", t.label)}>Total</span>
            <span
              data-testid="quote-total"
              className={cn("text-3xl md:text-4xl font-medium", t.total)}
              style={{ fontFamily: "Playfair Display, serif" }}
            >
              {money(data.total)}
            </span>
          </div>

          {hasJourney && (
            <div
              data-testid="journey-summary"
              className={cn("mt-4 flex flex-wrap gap-2 text-xs")}
            >
              {vehicleName && (
                <Chip icon={Car} label={vehicleName} t={t} testid="journey-vehicle" />
              )}
              {meta.trip_km && (
                <Chip icon={MapPin} label={`${Math.round(meta.trip_km)} km`} t={t} testid="journey-distance" />
              )}
              {meta.trip_minutes && (
                <Chip icon={Clock} label={`${Math.round(meta.trip_minutes)} min`} t={t} testid="journey-duration" />
              )}
            </div>
          )}

          <button
            type="button"
            data-testid="quote-toggle-breakdown"
            onClick={() => setOpen((v) => !v)}
            className={cn("mt-4 flex items-center gap-1.5 text-[11px] font-semibold tracking-[0.22em] uppercase transition-colors", t.label, "hover:sjc-gold")}
          >
            {open ? "Hide breakdown" : "View fare breakdown"}
            <ChevronDown className={cn("w-3.5 h-3.5 transition-transform", open && "rotate-180")} strokeWidth={1.5} />
          </button>

          <AnimatePresence initial={false}>
            {open && (
              <motion.dl
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.28, ease: "easeOut" }}
                className="mt-3 space-y-2.5 overflow-hidden"
              >
                <QuoteRow label="Base Fare" value={money(li.base_fare)} t={t} testid="quote-base-fare" />
                {li.meet_greet_fee > 0 && (
                  <QuoteRow label="Meet & Greet" value={money(li.meet_greet_fee)} t={t} testid="quote-meet-greet" />
                )}
                {li.waiting_charge > 0 && (
                  <QuoteRow
                    label={`Waiting Time (${li.billable_waiting_minutes} min)`}
                    value={money(li.waiting_charge)}
                    t={t}
                    testid="quote-waiting"
                  />
                )}
                {li.child_seat_charge > 0 && (
                  <QuoteRow label={`Child Seats (${li.child_seats})`} value={money(li.child_seat_charge)} t={t} testid="quote-child-seats" />
                )}
                <QuoteRow label={`GST (${Math.round(data.gst_rate * 100)}%)`} value={money(data.gst)} t={t} testid="quote-gst" />
                <div className={cn("pt-2 mt-1 border-t", t.divide)}>
                  <QuoteRow label="Total (inc. GST)" value={money(data.total)} t={t} testid="quote-total-line" bold />
                </div>
              </motion.dl>
            )}
          </AnimatePresence>

          <p className={cn("mt-4 flex items-center gap-1.5 text-[11px]", t.sub)}>
            <Sparkles className="w-3 h-3" strokeWidth={1.5} />
            {data.match_type === "exact"
              ? "Fixed rate from our price list · GST included"
              : "Estimated premium fare · GST included"}
          </p>
        </>
      ) : (
        <p className={cn("mt-3 text-sm", t.sub)}>Calculating your fare...</p>
      )}
    </div>
  );
}

function Chip({ icon: Icon, label, t, testid }) {
  return (
    <span
      data-testid={testid}
      className={cn("inline-flex items-center gap-1.5 rounded-full px-3 py-1.5", t.chip, t.chipText)}
    >
      <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
      {label}
    </span>
  );
}

function QuoteRow({ label, value, t, testid, bold = false }) {
  return (
    <div data-testid={testid} className="flex items-center justify-between text-sm">
      <dt className={t.label}>{label}</dt>
      <dd className={cn(bold ? "font-semibold" : "font-medium", t.value)}>{value}</dd>
    </div>
  );
}
